import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AiProviderModelOption,
  AiProviderPluginCatalogItem,
  AiRoutingRole,
  UserAiProviderConfiguration,
} from '@claude-code-api/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CheckCircle2, Settings2 } from 'lucide-react';
import { toast } from 'sonner';

type FieldValue = string | number | boolean | null;
type RolePreferences = Record<
  AiRoutingRole,
  { enabled: boolean; fallbackEnabled: boolean }
>;

const DEFAULT_PREFERENCES: RolePreferences = {
  pm: { enabled: true, fallbackEnabled: true },
  coding: { enabled: true, fallbackEnabled: true },
};

function preferredPlugin(
  catalog: AiProviderPluginCatalogItem[],
  configurations: UserAiProviderConfiguration[],
) {
  const configuredPluginId = configurations[0]?.pluginCatalogId;
  return catalog.find(({ id }) => id === configuredPluginId) || catalog[0];
}

const CUSTOM_PROVIDER_PREFIX = 'custom_';

function isCustomProvider(value: FieldValue | undefined): boolean {
  return String(value || '').startsWith(CUSTOM_PROVIDER_PREFIX);
}

export function UserAiProviderSettings() {
  const [catalog, setCatalog] = useState<AiProviderPluginCatalogItem[]>([]);
  const [configurations, setConfigurations] = useState<
    UserAiProviderConfiguration[]
  >([]);
  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const [preferences, setPreferences] =
    useState<RolePreferences>(DEFAULT_PREFERENCES);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [models, setModels] = useState<AiProviderModelOption[]>([]);
  const [discoveringModels, setDiscoveringModels] = useState(false);
  const discoveryRequestId = useRef(0);
  const [modelChoice, setModelChoice] = useState('');
  const [manualModelOpen, setManualModelOpen] = useState(false);
  const [manualModel, setManualModel] = useState('');

  const load = useCallback(async () => {
    const [catalogResponse, configurationResponse] = await Promise.all([
      fetch('/api/provider-plugins/catalog', { credentials: 'include' }),
      fetch('/api/provider-plugins/me', { credentials: 'include' }),
    ]);
    if (!catalogResponse.ok || !configurationResponse.ok) throw new Error();
    const catalogPayload = (await catalogResponse.json()) as {
      data: AiProviderPluginCatalogItem[];
    };
    const configurationPayload = (await configurationResponse.json()) as {
      data: UserAiProviderConfiguration[];
    };
    setCatalog(catalogPayload.data);
    setConfigurations(configurationPayload.data);
    const existing = configurationPayload.data[0];
    if (existing) {
      setValues(existing.configuredFields);
      setModelChoice(existing.modelName);
    }
    const preferred = preferredPlugin(
      catalogPayload.data,
      configurationPayload.data,
    );
    if (preferred) {
      const modelsResponse = await fetch('/api/provider-plugins/models', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          pluginCatalogId: preferred.id,
          configuration: {},
        }),
      });
      if (modelsResponse.ok) {
        const modelsPayload = (await modelsResponse.json()) as {
          data?: AiProviderModelOption[];
        };
        setModels(modelsPayload.data || []);
      }
    }
    setPreferences({
      pm: {
        enabled:
          configurationPayload.data.find(({ role }) => role === 'pm')
            ?.enabled ?? true,
        fallbackEnabled:
          configurationPayload.data.find(({ role }) => role === 'pm')
            ?.fallbackEnabled ?? true,
      },
      coding: {
        enabled:
          configurationPayload.data.find(({ role }) => role === 'coding')
            ?.enabled ?? true,
        fallbackEnabled:
          configurationPayload.data.find(({ role }) => role === 'coding')
            ?.fallbackEnabled ?? true,
      },
    });
  }, []);

  useEffect(() => {
    void load()
      .catch(() => toast.error('Could not load AI provider settings'))
      .finally(() => setLoading(false));
  }, [load]);

  const plugin = useMemo(
    () => preferredPlugin(catalog, configurations),
    [catalog, configurations],
  );
  const activeConfiguration = configurations.find(({ enabled }) => enabled);
  const isConfigured = configurations.length > 0;
  const providerField = plugin?.configFields.find(
    ({ key }) => key === 'provider',
  );
  const customProvider = isCustomProvider(values.provider);
  const primaryFields =
    plugin?.configFields.filter(
      ({ key }) => key !== 'provider' && (key !== 'base_url' || customProvider),
    ) || [];

  useEffect(() => {
    if (!providerField || values.provider) return;
    const defaultProvider =
      providerField.options.find(({ value }) => value === 'openai') ||
      providerField.options[0];
    if (!defaultProvider) return;
    setValues((current) => ({
      ...current,
      provider: defaultProvider.value,
    }));
  }, [providerField, values.provider]);

  const discoverModels = async (): Promise<AiProviderModelOption[]> => {
    if (!plugin) return [];
    const response = await fetch('/api/provider-plugins/models', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        pluginCatalogId: plugin.id,
        configuration: values,
      }),
    });
    const payload = (await response.json()) as {
      data?: AiProviderModelOption[];
      error?: string;
    };
    if (!response.ok) throw new Error(payload.error || 'Connection failed');
    return payload.data || [];
  };

  useEffect(() => {
    if (!plugin) return;
    const requiredFieldsReady = plugin.configFields
      .filter(({ required }) => required)
      .every(
        ({ key, fieldType }) =>
          (fieldType === 'secret' && isConfigured) ||
          (values[key] !== '' &&
            values[key] !== null &&
            values[key] !== undefined),
      );
    const customBaseUrlReady =
      !customProvider || Boolean(String(values.base_url || '').trim());
    if (!requiredFieldsReady || !customBaseUrlReady) return;
    const hasConnectionInput = plugin.configFields.some(
      ({ key }) => values[key] !== '' && values[key] !== null,
    );
    if (!hasConnectionInput) return;
    const requestId = ++discoveryRequestId.current;
    const timer = window.setTimeout(() => {
      setDiscoveringModels(true);
      void discoverModels()
        .then((nextModels) => {
          if (requestId === discoveryRequestId.current) setModels(nextModels);
        })
        .catch(() => {
          if (requestId === discoveryRequestId.current) setModels([]);
        })
        .finally(() => {
          if (requestId === discoveryRequestId.current)
            setDiscoveringModels(false);
        });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [customProvider, isConfigured, plugin, values]);

  const save = async () => {
    if (!plugin) return;
    setSaving(true);
    try {
      let discoveredModels: AiProviderModelOption[] = [];
      try {
        discoveredModels = await discoverModels();
      } catch (error) {
        // Manual model IDs are valid for compatible endpoints without a
        // working /models implementation; discovery failure must not block save.
        if (!modelChoice.trim()) throw error;
      }
      for (const role of ['pm', 'coding'] as const) {
        const existing = configurations.find((item) => item.role === role);
        const modelName =
          modelChoice.trim() || existing?.modelName || discoveredModels[0]?.id;
        if (!modelName) throw new Error('No model was selected');
        const response = await fetch(`/api/provider-plugins/me/${role}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            pluginCatalogId: plugin.id,
            modelName,
            enabled: preferences[role].enabled,
            fallbackEnabled: preferences[role].fallbackEnabled,
            configuration: values,
          }),
        });
        if (!response.ok) {
          const payload = (await response.json().catch(() => ({}))) as {
            error?: string;
          };
          throw new Error(
            payload.error || 'Provider settings could not be saved',
          );
        }
      }
      await load();
      setAdvancedOpen(false);
      toast.success('Your AI provider is ready');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Connection failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading...</p>;
  }

  if (!plugin) {
    return (
      <p className="text-sm text-muted-foreground">
        Personal API access is unavailable right now.
      </p>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">My API provider</p>
            <p className="truncate text-sm text-muted-foreground">
              {activeConfiguration?.enabled
                ? `Using your API · ${activeConfiguration.modelName}`
                : isConfigured
                  ? 'Saved, using the GIFT provider'
                  : 'Use your own OpenAI-compatible API'}
            </p>
          </div>
          {activeConfiguration?.enabled && (
            <Badge variant="secondary">Active</Badge>
          )}
        </div>

        {providerField && (
          <div className="space-y-2">
            <Label htmlFor="provider-provider">Provider</Label>
            <Select
              value={String(values.provider || '')}
              onValueChange={(provider) => {
                setValues((current) => ({
                  ...current,
                  provider,
                  ...(!provider.startsWith(CUSTOM_PROVIDER_PREFIX)
                    ? { base_url: '' }
                    : {}),
                }));
                setModels([]);
                setModelChoice('');
              }}
              disabled={saving}
            >
              <SelectTrigger id="provider-provider">
                <SelectValue placeholder="Choose your API provider" />
              </SelectTrigger>
              <SelectContent>
                {providerField.options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {customProvider
                ? 'For gateways and self-hosted APIs. Enter the API base URL below.'
                : providerField.description}
            </p>
          </div>
        )}

        {primaryFields.map((field) => (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={`provider-${field.key}`}>{field.label}</Label>
            <Input
              id={`provider-${field.key}`}
              type={field.fieldType === 'secret' ? 'password' : 'text'}
              value={String(values[field.key] ?? '')}
              placeholder={
                field.fieldType === 'secret' && isConfigured
                  ? 'Saved - enter a new key to replace it'
                  : field.fieldType === 'url'
                    ? 'https://api.example.com/v1'
                    : undefined
              }
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  [field.key]: event.target.value,
                }))
              }
            />
            {field.description && (
              <p className="text-xs text-muted-foreground">
                {field.description}
              </p>
            )}
          </div>
        ))}

        <div className="space-y-2">
          <Label htmlFor="provider-model">Model</Label>
          <Select
            value={modelChoice}
            onValueChange={(value) => {
              if (value === '__manual__') {
                setManualModel(modelChoice);
                setManualModelOpen(true);
              } else {
                setModelChoice(value);
              }
            }}
            disabled={saving}
          >
            <SelectTrigger id="provider-model" aria-label="Provider model">
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {models.map((model) => (
                <SelectItem key={model.id} value={model.id}>
                  {model.name || model.id}
                </SelectItem>
              ))}
              {modelChoice && !models.some(({ id }) => id === modelChoice) && (
                <SelectItem value={modelChoice}>{modelChoice}</SelectItem>
              )}
              <SelectItem value="__manual__">
                Enter model ID manually…
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {discoveringModels
              ? 'Discovering available models…'
              : models.length > 0
                ? `${models.length} models available from this provider.`
                : 'Models appear after the connection details are valid. Manual IDs are also supported.'}
          </p>
        </div>

        <div className="flex flex-wrap justify-between gap-2 pt-1">
          <Button
            type="button"
            variant="ghost"
            className="gap-2"
            onClick={() => setAdvancedOpen(true)}
          >
            <Settings2 className="h-4 w-4" />
            Advanced settings
          </Button>
          <Button onClick={() => void save()} disabled={saving}>
            <CheckCircle2 className="mr-2 h-4 w-4" />
            {saving ? 'Checking...' : 'Save and use my API'}
          </Button>
        </div>
      </div>

      <Dialog open={advancedOpen} onOpenChange={setAdvancedOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Advanced AI routing</DialogTitle>
            <DialogDescription>
              Configure custom endpoints and choose how each AI role uses your
              account.
            </DialogDescription>
          </DialogHeader>
          <div className="divide-y">
            {(['pm', 'coding'] as const).map((role) => (
              <div key={role} className="space-y-4 py-4 first:pt-0">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label htmlFor={`${role}-own-provider`}>
                      {role === 'pm' ? 'PM AI' : 'Coding AI'}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Use your API for this role.
                    </p>
                  </div>
                  <Switch
                    id={`${role}-own-provider`}
                    checked={preferences[role].enabled}
                    onCheckedChange={(enabled) =>
                      setPreferences((current) => ({
                        ...current,
                        [role]: { ...current[role], enabled },
                      }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label htmlFor={`${role}-fallback`}>GIFT fallback</Label>
                    <p className="text-xs text-muted-foreground">
                      Use platform credits if your API fails.
                    </p>
                  </div>
                  <Switch
                    id={`${role}-fallback`}
                    checked={preferences[role].fallbackEnabled}
                    onCheckedChange={(fallbackEnabled) =>
                      setPreferences((current) => ({
                        ...current,
                        [role]: { ...current[role], fallbackEnabled },
                      }))
                    }
                  />
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdvancedOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void save()} disabled={saving}>
              {saving ? 'Checking...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={manualModelOpen} onOpenChange={setManualModelOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Use a custom model ID</DialogTitle>
            <DialogDescription>
              Use the exact model ID accepted by your provider when discovery is
              unavailable or the model is not listed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="provider-manual-model">Model ID</Label>
            <Input
              id="provider-manual-model"
              value={manualModel}
              placeholder="provider-model-name"
              onChange={(event) => setManualModel(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualModelOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!manualModel.trim()}
              onClick={() => {
                setModelChoice(manualModel.trim());
                setManualModelOpen(false);
              }}
            >
              Use model
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
